import { InputField } from "@/components/input/InputField";
import type { FormulaUnit } from "@rebuild/shared";
import { attemptToStripUnits, getFormulaErrorMessage, validateUserTypedFormula } from "@rebuild/shared";
import { forwardRef, useState } from "react";
import type { ChangeEvent } from "react";

interface QuantityInputProps {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onValidityChange?: (isValid: boolean) => void;
  correctUnit?: FormulaUnit;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  allowInvalidFormulas?: boolean;
}

export const QuantityInput = forwardRef<HTMLDivElement, QuantityInputProps>(
  ({ onChange, onValidityChange, correctUnit, disabled, className, inputClassName, allowInvalidFormulas }, ref) => {
    const [inputError, setInputError] = useState<string>();

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const { errorMessage } = validateUserTypedFormula(value, correctUnit);

      if (errorMessage) {
        setInputError(getFormulaErrorMessage(errorMessage, correctUnit));
        if (allowInvalidFormulas) {
          onValidityChange?.(true);
          onChange({ ...e, target: { ...e.target, value } });
        } else {
          onValidityChange?.(false);
        }
        return;
      }

      setInputError(undefined);
      onValidityChange?.(true);

      // Strip the correctUnit from the typed value so the parent stores a unit-less formula
      // (e.g. "5 SF" -> "5"). The unit is shown separately as a suffix.
      const stripped = correctUnit ? attemptToStripUnits(value, correctUnit) : undefined;
      onChange({ ...e, target: { ...e.target, value: stripped ?? value } });
    };

    return (
      <div ref={ref}>
        <InputField
          className={`h-7 w-full ${className}`}
          inputClassName={`w-22 h-7 shadow-none ${inputClassName}`}
          placeHolder="QTY*"
          onChange={handleChange}
          name="line-item-quantity"
          error={inputError}
          disable={disabled}
          unitSuffix={correctUnit}
        />
      </div>
    );
  },
);
